import { TFunction } from 'i18next';
import { capitalize } from 'lodash';
import moment from 'moment';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Image } from '../../../components/image/Image';
import { SvgCheckCircle } from '../../../icons';
import { Asset } from '../../../queries/ats/titles/types/asset';
import { ProgramSource, VodSource } from '../../../queries/ats/titles/types/sources';
import { getSeasons } from '../../../queries/ats/titles/utils/array-utils';
import { classes } from '../../../utils/commonUtils';
import { getTimeStatus } from '../details-call-to-action/utils';
import { useDetails } from '../details-context';
import { AvailableContext } from '../use-available-contexts';
import { InputContext, useInputContext } from '../use-input-context';
import { getPreferredSource } from '../utils';
import css from './details-source-item.module.scss';

export interface DetailsSourceItemProps {
  asset: Asset;
  context: AvailableContext;
}

export function DetailsSourceItem({ asset, context }: DetailsSourceItemProps) {
  const { selectedContext, setSelectedContextId } = useDetails('DetailsController');

  const inputContext = useInputContext();
  const subtitle = useSourceSubtitle(asset, context, inputContext);

  const isActive = context.isActive(selectedContext.id);

  return (
    <button className={css.container} data-active={isActive} onClick={() => setSelectedContextId(context.id)}>
      {typeof context.image === 'string' ? (
        <Image className={css.logo} src={context.image} alt={`${context.name} logo`} />
      ) : (
        <div className={css.logo}>{context.image}</div>
      )}
      <div className={css.text}>
        <p className={css.title}>{context.name}</p>
        {subtitle !== undefined && <p className={css.subtitle}>{subtitle}</p>}
      </div>
      <SvgCheckCircle className={css.icon} />
    </button>
  );
}

function useSourceSubtitle(asset: Asset, context: AvailableContext, inputContext: InputContext) {
  const { t } = useTranslation();

  if (asset.type === 'series') {
    const episodes = context.episodes(asset);
    const seasons = getSeasons(episodes);
    return seasons.length === 1
      ? t('common:episodes', { count: episodes.length })
      : t('common:seasons', { count: seasons.length });
  }

  const preferredSource = getPreferredSource(asset.sources, context, inputContext);

  if (preferredSource.type === 'program') {
    return programSourceSubtitle(preferredSource, t);
  }

  if (preferredSource.type === 'vod') {
    return vodSourceSubtitle(preferredSource, t);
  }

  if (preferredSource.type === 'svod' && !preferredSource.isSubscribed) {
    return t('details:get-access');
  }

  return undefined;
}

function programSourceSubtitle(source: ProgramSource, t: TFunction) {
  const status = getTimeStatus(source);
  const now = moment();

  if (now.isAfter(source.expiresAt)) {
    return t('common:expired');
  }

  switch (status) {
    case 'present':
      return t('details:live');
    case 'future':
      const startTime = moment(source.startTime);
      return capitalize(startTime.fromNow());
    case 'past':
      const expiresAt = moment(source.expiresAt);
      return t('time:time-left', { value: expiresAt.fromNow(true) });
  }
}

function vodSourceSubtitle(source: VodSource, t: TFunction) {
  switch (source.product.status) {
    case 'purchased':
      return t('common:purchased');
    case 'rented':
      return t('common:rented');
    case 'pending':
      // TODO: Temp fix for gjermund & co to test cvp.
      return t('common:from-price', { value: source.product.price?.amount ?? '???' });
  }
}

export function DetailsSourceItemSkeleton() {
  return (
    <button className={classes(css.item, css.skeleton)}>
      <div className={css.logo} />
      <div className={css.text}>
        <div className={css.asset} />
        <div className={css.subtitle} />
      </div>
    </button>
  );
}
